import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@stores/authStore'
import { apiClient } from '@services/api'
import { AuthLayout } from '@components/AuthLayout'

export function SignupPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const [form, setForm] = useState({ companyName: '', fullName: '', email: '', phone: '', password: '', loadDemoData: true })
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [selfSignup, setSelfSignup] = useState<boolean | null>(null)
  useEffect(() => { apiClient.get<any>('/auth/status').then((r) => setSelfSignup(!!r?.data?.selfSignup)).catch(() => setSelfSignup(false)) }, [])
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!form.companyName.trim()) return setError('Company name is required')
    if (!form.fullName.trim()) return setError('Your name is required')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError('Enter a valid email address')
    if (form.password.length < 8) return setError('Password must be at least 8 characters')
    if (form.phone && !/^\d{10}$/.test(form.phone)) return setError('Phone must be a 10-digit mobile number')
    setSubmitting(true)
    try {
      const data = await apiClient.signup({ ...form, companyName: form.companyName.trim(), fullName: form.fullName.trim(), email: form.email.trim() })
      setAuth(data.accessToken, data.refreshToken, data.user)
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not create the account')
    } finally {
      setSubmitting(false)
    }
  }

  if (selfSignup === false) {
    return (
      <AuthLayout>
        <div className="card w-full max-w-[440px] !p-8">
          <h1 className="text-[20px] font-bold tracking-[-0.01em] m-0">Get Xora FMCG Ops for your company</h1>
          <p className="text-[13px] text-ink-soft mt-2">New companies are onboarded by Xora India so your workspace, invoice series and owner login are set up correctly from day one.</p>
          <div className="mt-4 p-3 rounded-[6px] bg-primary-soft text-[13px]">
            Write to <a href="mailto:support@xoraindia.com" className="text-primary-500 font-semibold hover:underline">support@xoraindia.com</a> with your firm name, GSTIN and the owner's email — you'll receive your login the same day.
          </div>
          <div className="mt-5 text-[13px] text-ink-soft text-center">
            Already have an account? <Link to="/login" className="text-primary-500 font-semibold hover:underline">Sign in</Link>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="card w-full max-w-[440px] !p-8">
        <h1 className="text-[20px] font-bold tracking-[-0.01em] m-0">Create your company account</h1>
        <p className="text-[13px] text-ink-soft mt-1 mb-6">Your own workspace — suppliers, stock, billing and reports, kept separate from every other company.</p>

        <form onSubmit={handleSubmit} noValidate>
          {error && <div className="alert alert-error">{error}</div>}

          <label htmlFor="su-company">Company / Firm Name</label>
          <input id="su-company" type="text" value={form.companyName} onChange={set('companyName')} placeholder="e.g. Sri Murugan Traders" disabled={submitting} />

          <div className="row2 mt-[14px]">
            <div>
              <label htmlFor="su-name">Your Name</label>
              <input id="su-name" type="text" value={form.fullName} onChange={set('fullName')} placeholder="Owner / manager" disabled={submitting} />
            </div>
            <div>
              <label htmlFor="su-phone">
                Phone <small>— optional</small>
              </label>
              <input id="su-phone" type="text" value={form.phone} onChange={set('phone')} placeholder="10-digit mobile" disabled={submitting} />
            </div>
          </div>

          <label htmlFor="su-email" className="mt-[14px]">
            Email Address
          </label>
          <input id="su-email" type="email" autoComplete="username" value={form.email} onChange={set('email')} placeholder="you@company.com" disabled={submitting} />

          <label htmlFor="su-password" className="mt-[14px]">
            Password <small>— at least 8 characters</small>
          </label>
          <input id="su-password" type="password" autoComplete="new-password" value={form.password} onChange={set('password')} placeholder="••••••••" disabled={submitting} />

          <label className="mt-[14px] flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.loadDemoData} onChange={set('loadDemoData')} disabled={submitting} />
            <span>
              Load sample data to explore <small>— suppliers, bills, stock and repacking examples; you can clear it later</small>
            </span>
          </label>

          <button type="submit" disabled={submitting} className="btn-primary w-full mt-5">
            {submitting ? 'Creating your workspace…' : 'Create account'}
          </button>
        </form>

        <div className="mt-5 text-[13px] text-ink-soft text-center">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-500 font-semibold hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </AuthLayout>
  )
}
